import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Palette } from "lucide-react";
import type { LightboxProps } from "./types";

function formatDate(dateString?: string): string | null {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;

export function Lightbox({ images, currentIndex, onClose, onNavigate }: LightboxProps) {
  const navigate = useNavigate();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const currentImage = images[currentIndex];
  const uploadDate = formatDate(currentImage?.image?.created_at);

  const resetZoomPan = useCallback(() => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      onNavigate(currentIndex - 1);
      resetZoomPan();
    }
  }, [currentIndex, onNavigate, resetZoomPan]);

  const goToNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      onNavigate(currentIndex + 1);
      resetZoomPan();
    }
  }, [currentIndex, images.length, onNavigate, resetZoomPan]);

  const handleClose = useCallback(() => {
    onClose();
    resetZoomPan();
  }, [onClose, resetZoomPan]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoomLevel((prev) => {
      const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta));
      if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
      return newZoom;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoomLevel > 1) {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({
          x: e.clientX - panPosition.x,
          y: e.clientY - panPosition.y,
        });
      }
    },
    [zoomLevel, panPosition]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoomLevel > 1) {
        setPanPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, zoomLevel, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (zoomLevel > 1 && e.touches.length === 1) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - panPosition.x,
          y: e.touches[0].clientY - panPosition.y,
        });
      }
    },
    [zoomLevel, panPosition]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (isDragging && zoomLevel > 1 && e.touches.length === 1) {
        e.preventDefault();
        setPanPosition({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y,
        });
      }
    },
    [isDragging, zoomLevel, dragStart]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPrev();
      else if (e.key === "ArrowRight") goToNext();
      else if (e.key === "Escape") handleClose();
      else if (e.key === " ") {
        e.preventDefault();
        setZoomLevel((prev) => (prev === 1 ? 2 : 1));
        if (zoomLevel !== 1) setPanPosition({ x: 0, y: 0 });
      } else if (e.key === "+" || e.key === "=") {
        setZoomLevel((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
      } else if (e.key === "-" || e.key === "_") {
        setZoomLevel((prev) => {
          const newZoom = Math.max(MIN_ZOOM, prev - ZOOM_STEP);
          if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
          return newZoom;
        });
      } else if (e.key === "0") {
        resetZoomPan();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToPrev, goToNext, handleClose, zoomLevel, resetZoomPan]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!currentImage) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <motion.button
          className="absolute top-4 right-4 z-20 text-white text-4xl hover:text-[var(--color-primary)] transition-colors"
          onClick={handleClose}
          whileHover={{ scale: 1.1 }}
        >
          ×
        </motion.button>

        <div className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-black/50 rounded-full px-3 py-2 backdrop-blur-sm">
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 text-white text-xl transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setZoomLevel((prev) => {
                const newZoom = Math.max(MIN_ZOOM, prev - ZOOM_STEP);
                if (newZoom <= 1) setPanPosition({ x: 0, y: 0 });
                return newZoom;
              });
            }}
          >
            −
          </button>
          <span className="text-white text-sm min-w-[3rem] text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 text-white text-xl transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setZoomLevel((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
            }}
          >
            +
          </button>
          <button
            className="ml-2 px-2 py-1 rounded hover:bg-white/20 text-white text-xs transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              resetZoomPan();
            }}
          >
            Reset
          </button>
        </div>

        {currentIndex > 0 && (
          <motion.button
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl transition-colors backdrop-blur-sm"
            onClick={(e) => {
              e.stopPropagation();
              goToPrev();
            }}
            whileHover={{ scale: 1.1 }}
          >
            ‹
          </motion.button>
        )}

        {currentIndex < images.length - 1 && (
          <motion.button
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl transition-colors backdrop-blur-sm"
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            whileHover={{ scale: 1.1 }}
          >
            ›
          </motion.button>
        )}

        <div
          ref={imageContainerRef}
          className={`relative select-none ${
            zoomLevel > 1
              ? isDragging
                ? "cursor-grabbing"
                : "cursor-grab"
              : "cursor-zoom-in"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (zoomLevel === 1) {
              setZoomLevel(2);
            }
          }}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleMouseUp}
          style={{
            transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
            transition: isDragging ? "none" : "transform 0.15s ease-out",
          }}
        >
          <motion.img
            src={currentImage.image.url}
            alt={currentImage.image.altText || `Art by ${currentImage.artist.name}`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg pointer-events-none"
            draggable={false}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          />
        </div>

        <motion.div
          className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent z-10"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold">{currentImage.artist.name}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/70 text-sm">
                {uploadDate && <span>{uploadDate}</span>}
                {currentImage.image.tags && currentImage.image.tags.length > 0 && (
                  <span className="truncate">
                    {currentImage.image.tags.join(" • ")}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  navigate(`/commission?artist=${currentImage.artist.slug}`);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] text-sm font-medium hover:bg-[var(--color-primary-light)] transition-colors whitespace-nowrap"
              >
                <Palette className="w-4 h-4" />
                <span className="hidden sm:inline">Commission {currentImage.artist.name}</span>
                <span className="sm:hidden">Commission</span>
              </button>
              <div className="hidden md:flex items-center gap-4 text-white/60 text-sm">
                <span>
                  {currentIndex + 1} / {images.length}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
